import React, { useEffect, useState } from 'react';
import { Card } from './ui/Card';
import { TrendingUp, ShoppingCart, UserPlus, Eye, RefreshCcw, Award, Mail, Info, Zap, ArrowRight, BarChart3, Percent } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from './ui/Container';
type FlowMetric = {
  name: string;
  icon: React.ElementType;
  description: string;
  monthlyRevenue: number;
  metrics: {
    triggerLabel: string;
    triggerCount: number;
    conversionRate: string;
    conversionCount: number;
  };
};
export function ROICalculator() {
  // Inputs
  const [visitors, setVisitors] = useState<number>(50000);
  const [aov, setAov] = useState<number>(85);
  const [listSize, setListSize] = useState<number>(25000);
  const [upliftPercentage, setUpliftPercentage] = useState<number>(20);
  // Results State
  const [flows, setFlows] = useState<FlowMetric[]>([]);
  const [revenueWithoutFlowsense, setRevenueWithoutFlowsense] = useState(0);
  const [revenueUplift, setRevenueUplift] = useState(0);
  const [totalRevenueWithFlowsense, setTotalRevenueWithFlowsense] = useState(0);
  const calculateRevenue = () => {
    // 1. Welcome Series
    // Assumption: 4% of visitors subscribe
    const newSubscribers = Math.round(visitors * 0.04);
    const welcomeConversionRate = 0.09; // 9%
    const welcomeConversions = Math.round(newSubscribers * welcomeConversionRate);
    const welcomeRevenue = welcomeConversions * aov;
    // 2. Abandoned Cart
    // Assumption: 10% of visitors add to cart
    const cartsCreated = Math.round(visitors * 0.1);
    const cartTriggers = Math.round(cartsCreated * 0.65); // 65% trigger rate
    const cartRecoveryRate = 0.175; // 17.5%
    const cartConversions = Math.round(cartTriggers * cartRecoveryRate);
    const cartRevenue = cartConversions * aov;
    // 3. Browse Abandonment
    // Assumption: 20% of visitors are trackable browsers who don't cart
    const browsers = Math.round(visitors * 0.2);
    const browseTriggers = Math.round(browsers * 0.45); // 45% trigger rate
    const browseConversionRate = 0.1; // 10%
    const browseConversions = Math.round(browseTriggers * browseConversionRate);
    const browseRevenue = browseConversions * aov;
    // 4. Post-purchase / Cross-sell
    // Assumption: 2.5% conversion rate from visitor to buyer
    const purchasers = Math.round(visitors * 0.025);
    const postPurchaseTriggers = purchasers; // 100% trigger rate
    const repeatPurchaseRate = 0.125; // 12.5%
    const postPurchaseConversions = Math.round(postPurchaseTriggers * repeatPurchaseRate);
    const postPurchaseRevenue = postPurchaseConversions * aov;
    // 5. Win-back / Re-engagement
    // Assumption: 30% of list is inactive/at-risk
    const inactiveSubscribers = Math.round(listSize * 0.3);
    const winbackTriggers = Math.round(inactiveSubscribers * 0.2); // 20% trigger rate
    const winbackConversionRate = 0.04; // 4%
    const winbackConversions = Math.round(winbackTriggers * winbackConversionRate);
    const winbackRevenue = winbackConversions * aov;
    // 6. Loyalty / Rewards
    // Based on purchasers
    const loyaltyTriggers = Math.round(purchasers * 0.25); // 25% of customers
    const loyaltyConversionRate = 0.065; // 6.5%
    const loyaltyConversions = Math.round(loyaltyTriggers * loyaltyConversionRate);
    const loyaltyRevenue = loyaltyConversions * aov;
    // Calculate Totals
    const baselineRevenue = welcomeRevenue + cartRevenue + browseRevenue + postPurchaseRevenue + winbackRevenue + loyaltyRevenue;
    const upliftAmount = baselineRevenue * (upliftPercentage / 100);
    const totalWithFlowsense = baselineRevenue + upliftAmount;
    // Update State
    setRevenueWithoutFlowsense(baselineRevenue);
    setRevenueUplift(upliftAmount);
    setTotalRevenueWithFlowsense(totalWithFlowsense);
    // Update Flow Breakdown (showing baseline numbers for the breakdown cards)
    // We could optionally show the "uplifted" numbers in the cards, but keeping them as baseline
    // makes the "Uplift" metric stand out more as the "FlowSense Effect".
    // Let's keep cards as baseline for clarity of where the money comes from.
    setFlows([{
      name: 'Welcome Series',
      icon: UserPlus,
      description: 'First impression for new subscribers',
      monthlyRevenue: welcomeRevenue,
      metrics: {
        triggerLabel: 'New Subscribers',
        triggerCount: newSubscribers,
        conversionRate: '9%',
        conversionCount: welcomeConversions
      }
    }, {
      name: 'Abandoned Cart',
      icon: ShoppingCart,
      description: 'Recover lost sales from carts',
      monthlyRevenue: cartRevenue,
      metrics: {
        triggerLabel: 'Cart Abandoners',
        triggerCount: cartTriggers,
        conversionRate: '17.5%',
        conversionCount: cartConversions
      }
    }, {
      name: 'Browse Abandonment',
      icon: Eye,
      description: 'Re-engage high-intent browsers',
      monthlyRevenue: browseRevenue,
      metrics: {
        triggerLabel: 'Active Browsers',
        triggerCount: browseTriggers,
        conversionRate: '10%',
        conversionCount: browseConversions
      }
    }, {
      name: 'Post-Purchase',
      icon: RefreshCcw,
      description: 'Cross-sell and repeat purchases',
      monthlyRevenue: postPurchaseRevenue,
      metrics: {
        triggerLabel: 'Recent Buyers',
        triggerCount: postPurchaseTriggers,
        conversionRate: '12.5%',
        conversionCount: postPurchaseConversions
      }
    }, {
      name: 'Win-back',
      icon: Mail,
      description: 'Re-activate dormant subscribers',
      monthlyRevenue: winbackRevenue,
      metrics: {
        triggerLabel: 'At-risk Contacts',
        triggerCount: winbackTriggers,
        conversionRate: '4%',
        conversionCount: winbackConversions
      }
    }, {
      name: 'Loyalty Program',
      icon: Award,
      description: 'Drive rewards program signups',
      monthlyRevenue: loyaltyRevenue,
      metrics: {
        triggerLabel: 'Eligible Customers',
        triggerCount: loyaltyTriggers,
        conversionRate: '6.5%',
        conversionCount: loyaltyConversions
      }
    }]);
  };
  useEffect(() => {
    calculateRevenue();
  }, [visitors, aov, listSize, upliftPercentage]);
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short'
    }).format(value);
  };
  return <div className="bg-gradient-to-br from-gray-50 to-blue-50/50 py-16 lg:py-24" data-id="element-209">
      <Container data-id="element-210">
        <div className="max-w-7xl mx-auto" data-id="element-211">
          <div className="text-center mb-12" data-id="element-212">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-213">
              Calculate Your Flow Potential
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-214">
              See how much additional revenue you could be generating with
              automated behavioral-driven email journeys optimized by Flowsense.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-id="element-215">
            {/* Left Column: Inputs */}
            <div className="lg:col-span-4 space-y-6" data-id="element-216">
              <Card className="p-6 lg:p-8 shadow-xl border-t-4 border-t-black sticky top-8" data-id="element-217">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-218">
                  <BarChart3 className="w-5 h-5 mr-2 text-gray-500" data-id="element-219" />
                  Your Metrics
                </h3>

                <div className="space-y-8" data-id="element-220">
                  {/* Visitors Input */}
                  <div data-id="element-221">
                    <div className="flex justify-between items-center mb-3" data-id="element-222">
                      <label className="text-sm font-semibold text-gray-700" data-id="element-223">
                        Monthly Site Visitors
                      </label>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold bg-gray-100 text-gray-900" data-id="element-224">
                        {formatNumber(visitors)}
                      </span>
                    </div>
                    <input type="range" min="5000" max="500000" step="5000" value={visitors} onChange={e => setVisitors(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black" data-id="element-225" />
                    <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium" data-id="element-226">
                      <span data-id="element-227">5k</span>
                      <span data-id="element-228">500k</span>
                    </div>
                  </div>

                  {/* AOV Input */}
                  <div data-id="element-229">
                    <div className="flex justify-between items-center mb-3" data-id="element-230">
                      <label className="text-sm font-semibold text-gray-700" data-id="element-231">
                        Average Order Value
                      </label>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold bg-gray-100 text-gray-900" data-id="element-232">
                        ${aov}
                      </span>
                    </div>
                    <input type="range" min="20" max="300" step="5" value={aov} onChange={e => setAov(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black" data-id="element-233" />
                    <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium" data-id="element-234">
                      <span data-id="element-235">$20</span>
                      <span data-id="element-236">$300</span>
                    </div>
                  </div>

                  {/* List Size Input */}
                  <div data-id="element-237">
                    <div className="flex justify-between items-center mb-3" data-id="element-238">
                      <label className="text-sm font-semibold text-gray-700" data-id="element-239">
                        Email List Size
                      </label>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold bg-gray-100 text-gray-900" data-id="element-240">
                        {formatNumber(listSize)}
                      </span>
                    </div>
                    <input type="range" min="1000" max="200000" step="1000" value={listSize} onChange={e => setListSize(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black" data-id="element-241" />
                    <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium" data-id="element-242">
                      <span data-id="element-243">1k</span>
                      <span data-id="element-244">200k</span>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-100" data-id="element-245">
                    <div className="flex justify-between items-center mb-3" data-id="element-246">
                      <label className="text-sm font-bold text-blue-600 flex items-center" data-id="element-247">
                        <Zap className="w-4 h-4 mr-1.5" data-id="element-248" />
                        Target Revenue Uplift
                      </label>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold bg-blue-50 text-blue-700 border border-blue-100" data-id="element-249">
                        {upliftPercentage}%
                      </span>
                    </div>
                    <input type="range" min="5" max="40" step="1" value={upliftPercentage} onChange={e => setUpliftPercentage(Number(e.target.value))} className="w-full h-2 bg-blue-100 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-250" />
                    <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium" data-id="element-251">
                      <span data-id="element-252">5%</span>
                      <span data-id="element-253">40%</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column: Results */}
            <div className="lg:col-span-8 space-y-8" data-id="element-254">
              {/* Top Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-255">
                {/* Metric 1: Without Flowsense */}
                <Card className="p-5 border-gray-200 bg-gray-50 h-full flex flex-col" data-id="element-256">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-257">
                    Revenue Without Flowsense
                  </p>
                  <p className="text-2xl font-bold text-gray-600" data-id="element-258">
                    {formatCurrency(revenueWithoutFlowsense)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-259">
                    Standard performance
                  </p>
                </Card>

                {/* Metric 2: Uplift (Star Metric) */}
                <Card className="p-5 border-blue-200 bg-blue-50 relative overflow-hidden ring-2 ring-blue-500 ring-offset-2 h-full flex flex-col" data-id="element-260">
                  <div className="absolute top-0 right-0 p-3 opacity-10" data-id="element-261">
                    <TrendingUp className="w-16 h-16 text-blue-600" data-id="element-262" />
                  </div>
                  <div className="relative z-10" data-id="element-263">
                    <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2 flex items-center" data-id="element-264">
                      <Zap className="w-3 h-3 mr-1" data-id="element-265" />
                      Revenue Uplift
                    </p>
                    <p className="text-3xl font-extrabold text-blue-600" data-id="element-266">
                      +{formatCurrency(revenueUplift)}
                    </p>
                    <p className="text-xs text-blue-600/80 mt-1 font-medium" data-id="element-267">
                      Additional monthly revenue
                    </p>
                  </div>
                </Card>

                {/* Metric 3: Total With Flowsense */}
                <Card className="p-5 border-gray-200 bg-white shadow-md h-full flex flex-col" data-id="element-268">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2" data-id="element-269">
                    Total Revenue With Flowsense
                  </p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-270">
                    {formatCurrency(totalRevenueWithFlowsense)}
                  </p>
                  <p className="text-xs text-green-600 mt-1 font-medium flex items-center" data-id="element-271">
                    <TrendingUp className="w-3 h-3 mr-1" data-id="element-272" />
                    {upliftPercentage}% Increase
                  </p>
                </Card>
              </div>

              {/* Flow Breakdown Grid */}
              <div data-id="element-273">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-274">
                  <Info className="w-5 h-5 mr-2 text-gray-400" data-id="element-275" />
                  Projected Revenue by Flow Type
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-276">
                  {flows.map(flow => <div key={flow.name} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-id="element-277">
                      <div className="flex justify-between items-start mb-3" data-id="element-278">
                        <div className="flex items-center" data-id="element-279">
                          <div className="p-2 bg-gray-50 rounded-lg mr-3 text-gray-700" data-id="element-280">
                            <flow.icon className="h-5 w-5" data-id="element-281" />
                          </div>
                          <div data-id="element-282">
                            <h4 className="font-bold text-gray-900 text-sm" data-id="element-283">
                              {flow.name}
                            </h4>
                            <p className="text-[10px] text-gray-500 uppercase tracking-wide" data-id="element-284">
                              {flow.metrics.triggerLabel}
                            </p>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-285">
                          <p className="font-bold text-gray-900" data-id="element-286">
                            {formatCurrency(flow.monthlyRevenue)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-50" data-id="element-287">
                        <span data-id="element-288">
                          {formatNumber(flow.metrics.triggerCount)} triggers
                        </span>
                        <span data-id="element-289">{flow.metrics.conversionRate} conv. rate</span>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section - Full Width Below Grid */}
          <div className="mt-8 bg-gray-900 rounded-2xl p-8 text-center relative overflow-hidden" data-id="element-290">
            <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-291"></div>
            <div className="relative z-10" data-id="element-292">
              <h3 className="text-2xl font-bold text-white mb-3 text-center" data-id="element-293">
                Ready to capture this extra revenue?
              </h3>
              <p className="text-gray-300 mb-8 max-w-xl mx-auto text-center" data-id="element-294">
                Stop leaving money on the table. Let Flowsense optimize your
                email journeys automatically.
              </p>

              <a href="https://go.useflowsense.com/signup" className="inline-block" data-id="element-295">
                <button className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-md bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-[length:200%_auto] text-white hover:bg-right-center shadow-lg transition-all duration-200 group animate-gradient" data-id="element-296">
                  Start Optimizing Now
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-297" />
                </button>
              </a>
            </div>
          </div>
        </div>
      </Container>
    </div>;
}